import type { JobViewModelClassification } from 'src/types/JobViewModel.ts';
import { useSelector } from 'src/store/react';
import { selectNormalisedCompanyName } from 'src/store/results/results';
import { useQualifiedLinkParams } from 'src/modules/qualified-location';
import { useJobCardEnrichedLocation } from 'src/modules/enriched-location';
import { useLinkProps } from '../../../../NavLink/NavLink.tsx';
import type { SearchParams } from '@seek/chalice-types';
import type { JobCardLinkProps } from '@seek/job-card';

export function useClassificationLinks({
  classification: jobClassification,
}: {
  classification: JobViewModelClassification;
}) {
  const normalisedCompanyName = useSelector(selectNormalisedCompanyName);
  const rel = 'nofollow';

  const getQualifiedLinkParams = useQualifiedLinkParams({
    rel,
    shouldUseUrlResolver: true,
  });
  const getJobCardEnrichedLocation = useJobCardEnrichedLocation();
  const getLinkProps = useLinkProps({
    getQualifiedLinkParams,
    rel,
  });

  const { classification, subClassification } = jobClassification;
  return [classification, subClassification].reduce<JobCardLinkProps[]>(
    (acc, classificationItem) => {
      if (!classificationItem) {
        return acc;
      }

      const { name, title, params: query } = classificationItem;

      const normalisedQuery: SearchParams = normalisedCompanyName
        ? {
            ...query,
            keywords: normalisedCompanyName,
          }
        : query;

      const linkProps = getLinkProps(
        getJobCardEnrichedLocation({
          pathname: '/jobs',
          query: normalisedQuery,
        }),
      );

      acc.push({
        name,
        title,
        ...linkProps,
      });

      return acc;
    },
    [],
  );
}
